// Statistik-Übersicht — alle Daten anonym aus telemetry.json.
// Charts sind reine HTML/CSS-Balken (keine externe Chart-Library = kein zusätzliches Gewicht).

import { useEffect, useMemo, useState } from 'react';
import { getStats, type FragenProPraxis, type PatientenProPraxis, type Stats } from '../api';

// Vite-Bundling: alle Rezept-JSONs zur Build-Zeit einlesen, sodass wir
// `rezept_id → titel` zur Anzeige im Chart mappen können. Sonst stehen in
// den Listen kryptische IDs wie "allgemeine-erkaeltungssymptome-wie-..."
// Glob-Pfad ist relativ zu dieser Datei — content liegt 4 Ebenen höher.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const REZEPT_FILES: Record<string, any> = (import.meta as any).glob(
  '../../../../content/rezepte/*.json',
  { eager: true },
);

function buildRezeptTitelMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const path in REZEPT_FILES) {
    const data = REZEPT_FILES[path].default ?? REZEPT_FILES[path];
    if (data?.id && data?.titel) {
      map[data.id] = data.titel;
    }
  }
  return map;
}

export default function Statistik() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [patientenProPraxis, setPatientenProPraxis] = useState<PatientenProPraxis[]>([]);
  const [fragenProPraxis, setFragenProPraxis] = useState<FragenProPraxis[]>([]);
  const [err, setErr] = useState<string | null>(null);
  // Mapping rezept_id → menschen-lesbarer Titel. Wird einmal beim Mount
  // aus den bundled JSONs gebaut.
  const rezeptTitel = useMemo(buildRezeptTitelMap, []);
  const titelOderId = (id: string) => rezeptTitel[id] ?? id;

  const reload = async () => {
    try {
      const r = await getStats();
      setStats(r.stats);
      setPatientenProPraxis(r.patienten_pro_praxis ?? []);
      setFragenProPraxis(r.fragen_pro_praxis ?? []);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };
  useEffect(() => { reload(); }, []);

  if (err) {
    return (
      <>
        <h1 className="page-title">Statistik</h1>
        <div className="card" style={{ color: '#D9534F' }}>Fehler: {err}</div>
      </>
    );
  }
  if (!stats) {
    return (
      <>
        <h1 className="page-title">Statistik</h1>
        <p className="page-sub">Wird geladen…</p>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Statistik</h1>
          <p className="page-sub">
            Anonyme Auswertung. Erstellt am {new Date(stats.generated_at).toLocaleString('de-DE')} ·{' '}
            {stats.total_events.toLocaleString('de-DE')} Events insgesamt.
          </p>
        </div>
        <button className="btn btn-ghost" onClick={reload}>↻ Neu laden</button>
      </div>

      {/* Kennzahlen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <Stat label="Rezept-Aufrufe (30T)" value={stats.rezept_views.last_30_days} accent="#00A099" />
        <Stat label="Rezept-Aufrufe (7T)" value={stats.rezept_views.last_7_days} accent="#00A099" />
        <Stat label="Krankmeldungen (30T)" value={stats.krankmeldungen.last_30_days} accent="#E99754" />
        <Stat
          label="Ø Krankmeldungs-Dauer"
          value={stats.krankmeldungen.durchschnitt_tage ?? '—'}
          unit={stats.krankmeldungen.durchschnitt_tage != null ? ' Tage' : ''}
          accent="#E99754"
        />
        <Stat label="QR-Scans (30T)" value={stats.praxis_qr_scans.last_30_days} accent="#7BB592" />
      </div>

      {/* Top Rezepte */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="section-title" style={{ marginTop: 0 }}>Top Rezept-Aufrufe (letzte 30 Tage)</h3>
        {stats.rezept_views.top_rezepte.length === 0 ? (
          <p className="page-sub" style={{ margin: 0 }}>Noch keine Daten — sammelt sich, sobald Patienten Rezepte öffnen.</p>
        ) : (
          <BarList items={stats.rezept_views.top_rezepte.map((r) => ({ label: titelOderId(r.rezept_id), value: r.count }))} color="#00A099" />
        )}
      </div>

      {/* Pro-Tag-Charts auf volle Breite — bei 31 Balken sind 2 Spalten zu eng
          und die Datums-Labels überlappen sich. */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="section-title" style={{ marginTop: 0 }}>Aufrufe pro Tag (letzte 30 Tage)</h3>
        <TimeChart items={stats.rezept_views.pro_tag} color="#00A099" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="section-title" style={{ marginTop: 0 }}>Krankmeldungen pro Tag (letzte 30 Tage)</h3>
        <TimeChart items={stats.krankmeldungen.pro_tag} color="#E99754" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Source */}
        <div className="card">
          <h3 className="section-title" style={{ marginTop: 0 }}>Aufrufe nach Quelle</h3>
          {stats.rezept_views.pro_source.length === 0 ? (
            <p className="page-sub" style={{ margin: 0 }}>Noch keine Daten.</p>
          ) : (
            <BarList items={stats.rezept_views.pro_source.map((s) => ({ label: sourceLabel(s.source), value: s.count }))} color="#5F6B68" />
          )}
        </div>

        {/* Krankmeldungs-Beschwerden */}
        <div className="card">
          <h3 className="section-title" style={{ marginTop: 0 }}>Häufige Krankmeldungs-Beschwerden</h3>
          {stats.krankmeldungen.pro_rezept.length === 0 ? (
            <p className="page-sub" style={{ margin: 0 }}>Noch keine Daten.</p>
          ) : (
            <BarList items={stats.krankmeldungen.pro_rezept.map((r) => ({ label: titelOderId(r.rezept_id), value: r.count }))} color="#E99754" />
          )}
        </div>
      </div>

      {/* Praxis-Statistik: anonyme Aggregat-Zahlen pro Praxis */}
      <h2 style={{ marginTop: 32, marginBottom: 4 }}>Praxis-Aktivität</h2>
      <p className="page-sub" style={{ marginBottom: 12 }}>
        Aggregat-Zahlen pro Praxis. Inhalte der Fragen oder Patientendaten sind hier nicht
        zugänglich — selbst dem System-Admin nicht.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <h3 className="section-title" style={{ marginTop: 0 }}>Verknüpfte Patienten pro Praxis</h3>
          {patientenProPraxis.length === 0 ? (
            <p className="page-sub" style={{ margin: 0 }}>
              Noch keine Patienten haben sich per QR-Code mit einer Praxis verknüpft.
            </p>
          ) : (
            <BarList
              items={patientenProPraxis
                .sort((a, b) => b.patienten_count - a.patienten_count)
                .map((p) => ({ label: p.praxis_name, value: p.patienten_count }))}
              color="#00A099"
            />
          )}
        </div>

        <div className="card">
          <h3 className="section-title" style={{ marginTop: 0 }}>Q&amp;A-Threads pro Praxis</h3>
          {fragenProPraxis.length === 0 ? (
            <p className="page-sub" style={{ margin: 0 }}>Noch keine Fragen-Threads.</p>
          ) : (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#5F6B68', borderBottom: '1px solid #DDE9E8' }}>
                  <th style={{ padding: '6px 4px' }}>Praxis</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Offen</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Beantw.</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Gesch.</th>
                  <th style={{ padding: '6px 4px', textAlign: 'right' }}>Nachr.</th>
                </tr>
              </thead>
              <tbody>
                {fragenProPraxis
                  .sort((a, b) => b.threads_total - a.threads_total)
                  .map((p) => (
                    <tr key={p.praxis_id} style={{ borderBottom: '1px solid #F2F8F8' }}>
                      <td style={{ padding: '8px 4px' }}>{p.praxis_name}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{p.threads_offen}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{p.threads_beantwortet}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>{p.threads_geschlossen}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', color: '#5F6B68' }}>{p.messages_total}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="info-card" style={{ marginTop: 24 }}>
        <h4>Was wird gesammelt?</h4>
        Bertsy speichert ausschließlich anonyme Ereignisse: welche <code>rezept_id</code> aufgerufen
        wurde, ob eine Krankmeldung angefordert wurde, die Anzahl Tage der Krankmeldung und ob diese
        an eine Praxis ging. Keine IP, Geräte-ID, Patient-Daten oder Beschwerde-Freitext.
        <br /><br />
        <strong>Patientenakten:</strong> Name, E-Mail und Geburtsdatum werden mit AES-256-GCM auf
        dem Server verschlüsselt und sind <strong>nur für die jeweilige Praxis</strong> einsehbar.
        Auch hier in der System-Statistik sehen wir nur die Anzahl pro Praxis, nie die Inhalte.
      </div>
    </>
  );
}

function sourceLabel(s: string): string {
  return { mobile: 'Mobile-App', embed: 'Embed-Widget', unknown: 'Unbekannt' }[s] ?? s;
}

function Stat({ label, value, unit, accent }: { label: string; value: number | string; unit?: string; accent: string }) {
  return (
    <div className="card" style={{ padding: 16, borderLeft: `4px solid ${accent}` }}>
      <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#1D1D1B', marginTop: 4 }}>
        {value}{unit ?? ''}
      </div>
    </div>
  );
}

function BarList({ items, color }: { items: { label: string; value: number }[]; color: string }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((it) => (
        <div key={it.label}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
            <span style={{ color: '#1D1D1B' }}>{it.label}</span>
            <span style={{ color: '#5F6B68', fontWeight: 600 }}>{it.value}</span>
          </div>
          <div style={{ height: 8, background: '#F2F8F8', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${(it.value / max) * 100}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TimeChart({ items, color }: { items: { date: string; count: number }[]; color: string }) {
  if (items.length === 0) return <p className="page-sub" style={{ margin: 0 }}>Noch keine Daten.</p>;
  const max = Math.max(...items.map((i) => i.count), 1);
  const total = items.reduce((sum, i) => sum + i.count, 0);
  const peak = items.reduce((m, i) => (i.count > m.count ? i : m), items[0]);

  // Bei 30 Tagen sind 31 Balken eng — Datums-Labels nur jeden N-ten Tag zeigen,
  // sonst überlappen sich die Texte. Erster + letzter Eintrag immer sichtbar.
  const labelEvery = items.length <= 7 ? 1 : items.length <= 14 ? 2 : 5;
  const formatDay = (iso: string) => {
    const d = new Date(iso + 'T12:00:00'); // Mittags-Parsing vermeidet Zeitzonen-Drift
    return `${d.getDate()}.${d.getMonth() + 1}.`;
  };
  const formatFullDay = (iso: string) => {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div>
      {/* Kennzahlen über dem Chart — geben sofort Kontext */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#5F6B68', marginBottom: 8 }}>
        <span>
          <strong style={{ color: '#1D1D1B' }}>{total.toLocaleString('de-DE')}</strong> gesamt in {items.length}&nbsp;Tagen
        </span>
        <span>
          Spitze: <strong style={{ color: '#1D1D1B' }}>{peak.count}</strong> am {formatDay(peak.date)}
        </span>
      </div>

      {/* Y-Achse + Balken */}
      <div style={{ display: 'flex', gap: 6 }}>
        {/* Y-Achsen-Skala */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: 10, color: '#5F6B68', textAlign: 'right', height: 120, paddingTop: 2 }}>
          <span>{max}</span>
          <span>{Math.round(max / 2)}</span>
          <span>0</span>
        </div>

        {/* Balken-Spalte */}
        <div style={{ flex: 1, position: 'relative' }}>
          {/* Gestrichelte Hilfslinien */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, borderTop: '1px dashed #DDE9E8' }} />
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed #DDE9E8' }} />
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderTop: '1px solid #DDE9E8' }} />
          </div>

          {/* Die Balken: alignItems flex-end → wachsen vom Boden nach oben.
              Jeder Balken bekommt einen Wrapper mit relativer Position; die
              Zahl wird absolut über dem Balken gerendert (kein Layout-Shift). */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, position: 'relative' }}>
            {items.map((it) => {
              const h = it.count === 0 ? 0 : Math.max(3, (it.count / max) * 100);
              return (
                <div
                  key={it.date}
                  title={`${formatFullDay(it.date)}: ${it.count} Aufruf${it.count === 1 ? '' : 'e'}`}
                  style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', position: 'relative', minWidth: 0 }}
                >
                  {it.count > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: `calc(${h}% + 2px)`,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: 9,
                        color: '#5F6B68',
                        fontWeight: 600,
                        lineHeight: 1,
                      }}
                    >
                      {it.count}
                    </span>
                  )}
                  <div
                    style={{
                      width: '100%',
                      height: `${h}%`,
                      background: color,
                      borderRadius: '3px 3px 0 0',
                      transition: 'height 0.3s',
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* X-Achsen-Labels — nur ausgewählte Tage, gleicher gap wie die Balken */}
          <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
            {items.map((it, idx) => {
              const visible = idx === 0 || idx === items.length - 1 || idx % labelEvery === 0;
              return (
                <div
                  key={it.date}
                  style={{
                    flex: 1,
                    fontSize: 10,
                    color: '#5F6B68',
                    textAlign: 'center',
                    minWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {visible ? formatDay(it.date) : ''}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
